import { ApolloError, useQuery } from '@apollo/client';
import type { GetFeatureTogglesQuery, GetFeatureTogglesQueryVariables } from 'generated/graphql';
import { FeatureQuery } from 'queries/Feature';
import type { FeaturesFragment } from 'types/graphqlFragment';

type FeatureFlags = {
  [key: string]: {
    isEnabled: boolean;
  };
};

type UseFeatureFlagsQueryResult = [FeatureFlags | null, boolean, ApolloError | undefined];

/**
 * Process the feature toggles into a more usable format. Feature flags are considered enabled if the value is ['*'].
 * @param {string} featureToggles - The feature toggles retrieved from the cache/server
 * @param {string[]} features - The specific features to query or undefined/empty to query all features
 * @returns
 */
const processFeatureToggles = (featureToggles: FeaturesFragment, features?: string[]): FeatureFlags => {
  const featureFlags: FeatureFlags = {};

  const shouldProcessAll = !features || features.length === 0;

  Object.entries(featureToggles).forEach(([key, value]) => {
    if (key !== '__typename' && Array.isArray(value) && (shouldProcessAll || features.includes(key))) {
      featureFlags[key] = { isEnabled: value[0] === '*' };
    }
  });

  return featureFlags;
};

/**
 * Get the feature flags from cache. If this is the first time the query is run, it will also fetch the feature flags from the server.
 * @param {string[]} features - A set of specific features to query or undefined/empty to query all features
 * @returns the feature flags as an array, loading state, and error state
 */
const useFeatureFlagsQuery = (features?: string[]): UseFeatureFlagsQueryResult => {
  const {
    data: featureFlagsData,
    loading,
    error
  } = useQuery<GetFeatureTogglesQuery, GetFeatureTogglesQueryVariables>(FeatureQuery);
  if (!featureFlagsData) {
    return [null, loading, error];
  }

  const { featureToggles } = featureFlagsData;
  if (!featureToggles) {
    return [null, loading, error];
  }

  const featureFlags = processFeatureToggles(featureToggles, features);

  return [featureFlags, loading, error];
};

export default useFeatureFlagsQuery;
